import { useEffect } from "react";

export interface SEOData {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath: string;
  ogType?: string;
  ogImage?: string;
  jsonLd?: Record<string, unknown>;
}

const siteUrl = (): string => {
  if (typeof import.meta !== "undefined" && import.meta.env?.VITE_SITE_URL) {
    return import.meta.env.VITE_SITE_URL as string;
  }
  return "https://hipobuyspreadsheets.site";
};

function upsertMeta(nameOrProp: string, content: string, isProperty: boolean): void {
  const selector = isProperty
    ? `meta[property="${nameOrProp}"]`
    : `meta[name="${nameOrProp}"]`;
  let meta = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!meta) {
    meta = document.createElement("meta");
    if (isProperty) {
      meta.setAttribute("property", nameOrProp);
    } else {
      meta.setAttribute("name", nameOrProp);
    }
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", content);
}

function upsertLink(rel: string, href: string): void {
  let link = document.head.querySelector(
    `link[rel="${rel}"]`
  ) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement("link");
    link.rel = rel;
    document.head.appendChild(link);
  }
  link.href = href;
}

function upsertJsonLd(data: Record<string, unknown>): void {
  const selector = 'script[type="application/ld+json"]';
  let script = document.head.querySelector(selector) as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement("script");
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data, null, 2);
}

export function useSEO(data: SEOData): void {
  useEffect(() => {
    const base = siteUrl();
    const fullUrl = `${base}${data.canonicalPath}`;

    document.title = data.title;

    upsertMeta("description", data.description, false);
    if (data.keywords) {
      upsertMeta("keywords", data.keywords, false);
    }

    upsertLink("canonical", fullUrl);

    upsertMeta("og:title", data.title, true);
    upsertMeta("og:description", data.description, true);
    upsertMeta("og:url", fullUrl, true);
    upsertMeta("og:type", data.ogType || "website", true);
    upsertMeta("og:locale", "fr_FR", true);
    if (data.ogImage) {
      upsertMeta("og:image", data.ogImage, true);
    }

    upsertMeta("twitter:card", "summary_large_image", true);
    upsertMeta("twitter:title", data.title, true);
    upsertMeta("twitter:description", data.description, true);

    if (data.jsonLd) {
      upsertJsonLd(data.jsonLd);
    }
  }, [
    data.title,
    data.description,
    data.keywords,
    data.canonicalPath,
    data.ogType,
    data.ogImage,
    data.jsonLd,
  ]);
}